/**
 * BadgeDisplay Component
 * 
 * High-performance badge display system with:
 * - Skeleton loading states for smooth UX
 * - Optimized image loading with fallbacks
 * - Graceful error handling
 * 
 * @author Neo - World-class performance optimization
 */

import React, { useState, useEffect, memo } from 'react';
import TenureBadge, { calculateYearsOfService } from './TenureBadge';
import employeeApi from '../lib/employeeApi';
import axios from 'axios';
import { Star, Trophy, Medal, Award, Crown, Heart, ThumbsUp, Zap, Flame, Target, Gem, Sparkles, Briefcase, Shield } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { formatDate } from '../lib/dateUtils';

const BACKEND_URL = '';

// Icon mapping for badges
const ICON_MAP = {
  star: Star,
  trophy: Trophy,
  medal: Medal,
  award: Award,
  crown: Crown,
  heart: Heart,
  'thumbs-up': ThumbsUp,
  zap: Zap,
  flame: Flame,
  target: Target,
  gem: Gem,
  sparkles: Sparkles,
  shield: Shield,
  briefcase: Briefcase,
};

/**
 * UNIFIED BADGE SIZE SYSTEM
 */
const BADGE_SIZES = {
  small: { container: 32, icon: 14, text: '9px', maxLabelWidth: 60 },
  medium: { container: 40, icon: 18, text: '10px', maxLabelWidth: 70 },
  large: { container: 48, icon: 22, text: '11px', maxLabelWidth: 80 },
};

/**
 * Resolves image URL for proper backend routing
 * Uses relative URLs so images stay on the same domain (e.g., girgit.app)
 */
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    // Convert /uploads/ to /api/uploads/ (relative URL)
    return `/api${url}`;
  } else if (url.startsWith('/api/uploads/')) {
    // Already correct format (relative URL)
    return url;
  } else if (url.startsWith('/')) {
    // Other relative paths, keep as-is
    return url;
  }
  // Absolute URLs (http/https) - return as-is
  return url;
};

/**
 * Skeleton Loader Component
 */
const SkeletonLoader = memo(({ width, height, borderRadius = '50%' }) => (
  <div
    className="animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"
    style={{
      width,
      height,
      borderRadius,
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite linear',
    }}
  />
));

SkeletonLoader.displayName = 'SkeletonLoader';

/**
 * Badge Image Component with loading state
 */
const BadgeImage = memo(({ 
  src, 
  alt, 
  size, 
  fallbackIcon: FallbackIcon, 
  fallbackColor,
  showBorder,
  borderColor 
}) => {
  const [loading, setLoading] = useState(!!src);
  const [error, setError] = useState(false);
  const Icon = FallbackIcon || Star;

  // Reset state when src changes
  useEffect(() => {
    if (src) {
      setLoading(true);
      setError(false);
    } else {
      setLoading(false);
      setError(false);
    }
  }, [src]);

  // If no src, show icon immediately
  if (!src) {
    return (
      <div 
        className="rounded-full flex items-center justify-center shadow-soft"
        style={{ 
          width: size.container, 
          height: size.container, 
          backgroundColor: fallbackColor 
        }}
      >
        <Icon size={size.icon} className="text-white" />
      </div>
    );
  }

  // Error state - show icon
  if (error) {
    return (
      <div 
        className="rounded-full flex items-center justify-center shadow-soft"
        style={{ 
          width: size.container, 
          height: size.container, 
          backgroundColor: fallbackColor 
        }}
      >
        <Icon size={size.icon} className="text-white" />
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: size.container, height: size.container }}>
      {/* Skeleton shown while loading */}
      {loading && (
        <div className="absolute inset-0">
          <SkeletonLoader width={size.container} height={size.container} />
        </div>
      )}
      
      {/* Actual image */}
      <div 
        className={`rounded-full overflow-hidden shadow-soft ${showBorder ? 'border-2' : ''}`}
        style={{ 
          width: size.container, 
          height: size.container, 
          borderColor: showBorder ? borderColor : 'transparent',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <img 
          src={src} 
          alt={alt} 
          className="w-full h-full object-cover"
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setError(true);
          }}
        />
      </div>
    </div>
  );
});

BadgeImage.displayName = 'BadgeImage';

/**
 * CustomBadge Component
 */
const CustomBadge = memo(({ badge, size = 'medium' }) => {
  const s = BADGE_SIZES[size] || BADGE_SIZES.medium;
  const Icon = ICON_MAP[badge.icon_type] || Star;
  
  const awardedDate = badge.awarded_at 
    ? formatDate(badge.awarded_at)
    : '';
  
  const rawImageUrl = badge.image_url || badge.badge_image_url || badge.imageUrl;
  const imageUrl = resolveImageUrl(rawImageUrl);
  
  const badgeColor = badge.color || badge.badge_color || '#6366F1';
  const badgeName = badge.name || badge.badge_name || 'Badge';
  const showBorder = badge.show_border !== false;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className="inline-flex flex-col items-center gap-0.5 cursor-pointer group"
            data-testid={`custom-badge-${badge.badge_id || badge._id}`}
          >
            <div className="relative group-hover:scale-110 transition-transform">
              <BadgeImage
                src={imageUrl}
                alt={badgeName}
                size={s}
                fallbackIcon={Icon}
                fallbackColor={badgeColor}
                showBorder={showBorder}
                borderColor={badgeColor}
              />
              
              {/* Multiple award count indicator */}
              {badge.count > 1 && (
                <div 
                  className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] rounded-full flex items-center justify-center text-white font-bold border border-white"
                  style={{ 
                    backgroundColor: badgeColor, 
                    fontSize: '9px',
                    padding: '0 3px'
                  }}
                >
                  {badge.count}
                </div>
              )}
            </div>
            
            <p 
              className="font-medium text-center leading-tight truncate"
              style={{ 
                fontSize: s.text, 
                color: badgeColor,
                maxWidth: s.maxLabelWidth
              }}
            >
              {badgeName}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="text-center">
            <p className="font-semibold">{badgeName}</p>
            {badge.awarded_for && <p className="text-sm text-neutral-600">{badge.awarded_for}</p>}
            {badge.reason && <p className="text-xs text-neutral-500 mt-1">{badge.reason}</p>}
            {awardedDate && <p className="text-xs text-neutral-400 mt-1">Awarded {awardedDate}</p>}
            {badge.count > 1 && <p className="text-xs text-indigo-600 mt-1 font-medium">Awarded {badge.count} times</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

CustomBadge.displayName = 'CustomBadge';

/**
 * DesignationBadge Component with hover popup
 */
const DesignationBadge = memo(({ badge, size = 'medium', allLevels = [] }) => {
  const s = BADGE_SIZES[size] || BADGE_SIZES.medium;
  const Icon = ICON_MAP[badge.icon_type] || Crown;
  const [showAllLevels, setShowAllLevels] = useState(false);
  
  const imageUrl = resolveImageUrl(badge.image_url || badge.imageUrl);
  const badgeColor = badge.color || '#F59E0B';
  const badgeName = badge.name || badge.level || 'Level';
  const showBorder = badge.show_border !== false;
  
  const sortedLevels = [...allLevels].sort((a, b) => (a.rank || 99) - (b.rank || 99));
  
  return (
    <div className="relative inline-block">
      <div 
        className="inline-flex flex-col items-center gap-0.5 cursor-pointer group"
        data-testid={`designation-badge-${badge.level}`}
        onMouseEnter={() => setShowAllLevels(true)}
        onMouseLeave={() => setShowAllLevels(false)}
      >
        <div className="group-hover:scale-110 transition-transform">
          <BadgeImage
            src={imageUrl}
            alt={badgeName}
            size={s}
            fallbackIcon={Icon}
            fallbackColor={badgeColor}
            showBorder={showBorder}
            borderColor={badgeColor}
          />
        </div>
        <p 
          className="font-medium text-center leading-tight truncate"
          style={{ 
            fontSize: s.text, 
            color: badgeColor,
            maxWidth: s.maxLabelWidth
          }}
        >
          {badgeName}
        </p>
      </div>
      
      {/* All Levels Popup */}
      {showAllLevels && sortedLevels.length > 0 && (
        <div 
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-white/40 p-4 min-w-[280px] max-w-[320px]"
          style={{ animation: 'fadeIn 0.2s ease-out' }}
          data-testid="designation-levels-popup"
        >
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-white/40 transform rotate-45" />
          
          <div className="relative mb-3 pb-2 border-b border-white/30">
            <h4 className="text-sm font-semibold text-[#1a1a2e] flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-amber-500" />
              Designation Levels
            </h4>
            <p className="text-xs text-neutral-500 mt-0.5">Your organization&apos;s hierarchy</p>
          </div>
          
          <div className="relative space-y-1.5 max-h-[300px] overflow-y-auto">
            {sortedLevels.map((level, idx) => {
              const levelImageUrl = resolveImageUrl(level.image_url);
              const LevelIcon = ICON_MAP[level.icon_type] || Crown;
              const isCurrentLevel = level.level === badge.level;
              const levelShowBorder = level.show_border !== false;
              
              return (
                <div 
                  key={level.level || idx}
                  className={`flex items-center gap-3 p-2 rounded-xl transition-colors ${
                    isCurrentLevel 
                      ? 'bg-amber-50 border border-amber-200 ring-1 ring-amber-300' 
                      : 'hover:bg-white/60'
                  }`}
                >
                  <div className="w-5 h-5 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-neutral-500">{level.rank || idx + 1}</span>
                  </div>
                  
                  <BadgeImage
                    src={levelImageUrl}
                    alt={level.name}
                    size={{ container: 32, icon: 14 }}
                    fallbackIcon={LevelIcon}
                    fallbackColor={level.color || '#6B7280'}
                    showBorder={levelShowBorder}
                    borderColor={level.color || '#6B7280'}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isCurrentLevel ? 'text-amber-800' : 'text-neutral-700'}`}>
                      {level.name || level.level}
                    </p>
                  </div>
                  
                  {isCurrentLevel && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded-full">
                      <span className="text-xs font-semibold text-amber-700">You</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="mt-3 pt-2 border-t border-white/30">
            <p className="text-xs text-neutral-400 text-center">
              Hover over badges to learn more
            </p>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
});

DesignationBadge.displayName = 'DesignationBadge';

/**
 * Badge Skeleton Component
 */
const BadgeSkeleton = memo(({ count = 3, size = 'medium' }) => {
  const s = BADGE_SIZES[size] || BADGE_SIZES.medium;
  
  return (
    <div className="flex items-start gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="inline-flex flex-col items-center gap-1">
          <SkeletonLoader width={s.container} height={s.container} />
          <div 
            className="animate-pulse bg-neutral-200 rounded"
            style={{ width: s.maxLabelWidth * 0.7, height: 10 }}
          />
        </div>
      ))}
    </div>
  );
});

BadgeSkeleton.displayName = 'BadgeSkeleton';

/**
 * BadgeDisplay Component - Main container
 */
const BadgeDisplay = ({ 
  employee, 
  size = 'medium', 
  showLabels = true,
  layout = 'horizontal',
  fetchBadges = true
}) => {
  const [customBadges, setCustomBadges] = useState([]);
  const [designationBadges, setDesignationBadges] = useState([]);
  const [allSystemBadges, setAllSystemBadges] = useState([]);
  const [settings, setSettings] = useState({
    show_tenure_badges: true,
    show_designation_badges: true,
    show_custom_badges: true
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  
  const firstWorkingDay = employee?.employment?.first_working_day || 
                          employee?.employment?.start_date ||
                          employee?.first_working_day;
  const yearsOfService = calculateYearsOfService(firstWorkingDay);
  const designationLevel = employee?.employment?.designation_level;
  
  // Fetch badge data - runs on mount and when key dependencies change
  useEffect(() => {
    let isMounted = true;
    
    const fetchAllData = async () => {
      setLoading(true);
      setFetchError(null);
      
      try {
        const defaultSettings = {
          show_tenure_badges: true,
          show_designation_badges: true,
          show_custom_badges: true
        };
        
        // Always fetch settings
        let settingsData = defaultSettings;
        try {
          const settingsRes = await axios.get(`${BACKEND_URL}/api/badge-settings`);
          settingsData = settingsRes.data || defaultSettings;
        } catch (e) {
          console.warn('Failed to fetch badge settings, using defaults');
        }
        
        if (isMounted) {
          setSettings(settingsData);
        }
        
        // Fetch custom badges if enabled
        if (fetchBadges) {
          try {
            const badgesRes = await employeeApi.get('/api/employee/badges');
            if (isMounted) {
              const badges = badgesRes.data?.badges || [];
              setCustomBadges(badges);
            }
          } catch (e) {
            console.warn('Failed to fetch employee badges:', e.message);
            if (isMounted) {
              setCustomBadges([]);
            }
          }
        }
        
        // Fetch designation badges if employee has a level
        if (designationLevel) {
          try {
            const systemRes = await axios.get(`${BACKEND_URL}/api/system-badges`);
            if (isMounted) {
              const systemBadges = systemRes.data?.system_badges || [];
              setAllSystemBadges(systemBadges);
              
              const myBadge = systemBadges.find(b => b.level === designationLevel);
              if (myBadge) {
                setDesignationBadges([myBadge]);
              }
            }
          } catch (e) {
            console.warn('Failed to fetch system badges:', e.message);
          }
        }
        
      } catch (error) {
        console.error('Error fetching badge data:', error);
        if (isMounted) {
          setFetchError(error.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchAllData();
    
    return () => {
      isMounted = false;
    };
  }, [fetchBadges, designationLevel]);
  
  const hasTenure = yearsOfService >= 1 && settings.show_tenure_badges;
  const hasDesignation = designationBadges.length > 0 && settings.show_designation_badges;
  const hasCustom = customBadges.length > 0 && settings.show_custom_badges;
  
  if (loading) {
    return <BadgeSkeleton count={3} size={size} />;
  }
  
  if (!hasTenure && !hasDesignation && !hasCustom) {
    return null;
  }
  
  const containerClass = layout === 'horizontal' 
    ? 'flex items-start gap-3 flex-wrap' 
    : 'flex flex-col items-center gap-2';
  
  return (
    <div className={containerClass} data-testid="badge-display">
      {hasTenure && (
        <TenureBadge 
          years={yearsOfService} 
          size={size} 
          showLabel={showLabels}
          animate={false}
        />
      )}
      
      {hasDesignation && designationBadges.map((badge) => (
        <DesignationBadge 
          key={badge.level} 
          badge={badge} 
          size={size} 
          allLevels={allSystemBadges}
        />
      ))}
      
      {settings.show_custom_badges && customBadges.map((badge) => (
        <CustomBadge key={badge._id || badge.badge_id} badge={badge} size={size} />
      ))}
    </div>
  );
};

export default BadgeDisplay;
export { CustomBadge, DesignationBadge, BadgeSkeleton, BADGE_SIZES, ICON_MAP, resolveImageUrl };
